import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AbstractForm } from '@core/forms/form';
import { B2bProgramUnit } from '../../../../shared/models/b2b-program-unit.model';
import { B2bProgramRate } from '../../../../shared/models/b2b-program-rate.model';
import { B2bProgramType } from '../../../../shared/models/b2b-program-type';


export enum ProgramSingleProductFormField {
    Name = 'name',
    TrfId = 'trfId',
    PrgType = 'prgType',
    CurrDiscountUnitType = 'currDiscountUnitType',
    CurrDiscountRateType = 'currDiscountRateType',
    CurrDiscountRate = 'currDiscountRate',
    NextDiscountDate = 'nextDiscountDate',
    NextDiscountUnitType = 'nextDiscountUnitType',
    NextDiscountRateType = 'nextDiscountRateType',
    NextDiscountRate = 'nextDiscountRate',
    CurrFeeUnitType = 'currFeeUnitType',
    CurrFeeRateType = 'currFeeRateType',
    CurrFeeRate = 'currFeeRate',
    NextFeeDate = 'nextFeeDate',
    NextFeeUnitType = 'nextFeeUnitType',
    NextFeeRateType = 'nextFeeRateType',
    NextFeeRate = 'nextFeeRate',
    OrgId = 'orgId',
}

export interface ProgramSingleProductFormModel {
    [ProgramSingleProductFormField.Name]: FormControl<string>;
    [ProgramSingleProductFormField.TrfId]: FormControl<number>;
    [ProgramSingleProductFormField.OrgId]: FormControl<number>;
    [ProgramSingleProductFormField.PrgType]: FormControl<B2bProgramType>;
    [ProgramSingleProductFormField.CurrDiscountUnitType]: FormControl<B2bProgramUnit>;
    [ProgramSingleProductFormField.CurrDiscountRateType]: FormControl<B2bProgramRate>;
    [ProgramSingleProductFormField.CurrDiscountRate]: FormControl<number>;
    [ProgramSingleProductFormField.NextDiscountDate]: FormControl<Date>;
    [ProgramSingleProductFormField.NextDiscountUnitType]: FormControl<B2bProgramUnit>;
    [ProgramSingleProductFormField.NextDiscountRateType]: FormControl<B2bProgramRate>;
    [ProgramSingleProductFormField.NextDiscountRate]: FormControl<number>;
    [ProgramSingleProductFormField.CurrFeeUnitType]: FormControl<B2bProgramUnit>;
    [ProgramSingleProductFormField.CurrFeeRateType]: FormControl<B2bProgramRate>;
    [ProgramSingleProductFormField.CurrFeeRate]: FormControl<number>;
    [ProgramSingleProductFormField.NextFeeDate]: FormControl<Date>;
    [ProgramSingleProductFormField.NextFeeUnitType]: FormControl<B2bProgramUnit>;
    [ProgramSingleProductFormField.NextFeeRateType]: FormControl<B2bProgramRate>;
    [ProgramSingleProductFormField.NextFeeRate]: FormControl<number>;
}

@Injectable()
export class ProgramSingleProductFormService extends AbstractForm<ProgramSingleProductFormField, ProgramSingleProductFormModel> {

    public createReactiveForm(): FormGroup<ProgramSingleProductFormModel> {
        this.build({
            [ProgramSingleProductFormField.Name]: new FormControl<string>(null, [Validators.required]),
            [ProgramSingleProductFormField.TrfId]: new FormControl<number>(null, [Validators.required]),
            [ProgramSingleProductFormField.PrgType]: new FormControl<B2bProgramType>(null, [Validators.required]),
            [ProgramSingleProductFormField.OrgId]: new FormControl<number>(null, [Validators.required]),
            [ProgramSingleProductFormField.CurrDiscountUnitType]: new FormControl<B2bProgramUnit>(null, [Validators.required]),
            [ProgramSingleProductFormField.CurrDiscountRateType]:
                new FormControl<B2bProgramRate>(B2bProgramRate.FixSum, [Validators.required]),
            [ProgramSingleProductFormField.CurrDiscountRate]: new FormControl<number>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextDiscountDate]: new FormControl<Date>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextDiscountUnitType]: new FormControl<B2bProgramUnit>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextDiscountRateType]:
                new FormControl<B2bProgramRate>(B2bProgramRate.FixSum, [Validators.required]),
            [ProgramSingleProductFormField.NextDiscountRate]: new FormControl<number>(null, [Validators.required]),
            [ProgramSingleProductFormField.CurrFeeUnitType]: new FormControl<B2bProgramUnit>(null, [Validators.required]),
            [ProgramSingleProductFormField.CurrFeeRateType]: new FormControl<B2bProgramRate>(B2bProgramRate.FixSum, [Validators.required]),
            [ProgramSingleProductFormField.CurrFeeRate]: new FormControl<number>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextFeeDate]: new FormControl<Date>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextFeeUnitType]: new FormControl<B2bProgramUnit>(null, [Validators.required]),
            [ProgramSingleProductFormField.NextFeeRateType]: new FormControl<B2bProgramRate>(B2bProgramRate.FixSum, [Validators.required]),
            [ProgramSingleProductFormField.NextFeeRate]: new FormControl<number>(null, [Validators.required]),
        });

        this.defineMechanics();

        return this.form;
    }

    public defineMechanicsAfterPatchForm(): void {
        this.getControlByPath(ProgramSingleProductFormField.TrfId).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrFeeRate).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrFeeRateType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrDiscountRate).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrDiscountRateType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.NextFeeRate).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.NextFeeRateType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.NextDiscountRate).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.NextDiscountRateType).clearValidators();

        this.getControlByPath(ProgramSingleProductFormField.NextDiscountUnitType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrDiscountUnitType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.CurrFeeUnitType).clearValidators();
        this.getControlByPath(ProgramSingleProductFormField.NextFeeUnitType).clearValidators();
    }

    private defineMechanics(): void {
        this.watchForValueChanges(ProgramSingleProductFormField.CurrDiscountRateType)
            .subscribe(() => this.getControlByPath(ProgramSingleProductFormField.CurrDiscountRate).updateValueAndValidity());

        this.watchForValueChanges(ProgramSingleProductFormField.NextDiscountRateType)
            .subscribe(() => this.getControlByPath(ProgramSingleProductFormField.NextDiscountRate).updateValueAndValidity());

        this.watchForValueChanges(ProgramSingleProductFormField.CurrFeeRateType)
            .subscribe(() => this.getControlByPath(ProgramSingleProductFormField.CurrFeeRate).updateValueAndValidity());

        this.watchForValueChanges(ProgramSingleProductFormField.NextFeeRateType)
            .subscribe(() => this.getControlByPath(ProgramSingleProductFormField.NextFeeRate).updateValueAndValidity());
    }
}
